import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Shield, Loader2, Check, X, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function UserPermissionsDialog({ open, onOpenChange, user, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [permissionGroups, setPermissionGroups] = useState({});
  
  // Form state
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [customPermissions, setCustomPermissions] = useState([]);
  const [permissionOverrides, setPermissionOverrides] = useState({});
  const [effectivePermissions, setEffectivePermissions] = useState([]);
  const [activeTab, setActiveTab] = useState('role');

  useEffect(() => {
    if (open && user) {
      fetchData();
    }
  }, [open, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes, userPermsRes] = await Promise.all([
        axios.get(`${API}/rbac/roles`),
        axios.get(`${API}/rbac/permissions`),
        axios.get(`${API}/rbac/users/${user.id}/permissions`)
      ]);
      
      setRoles(rolesRes.data.roles || []);
      setPermissions(permsRes.data.permissions || {});
      setPermissionGroups(permsRes.data.groups || {});
      
      const userPerms = userPermsRes.data;
      setSelectedRoleId(userPerms.role_id || '');
      setCustomPermissions(userPerms.custom_permissions || []);
      setPermissionOverrides(userPerms.permission_overrides || {});
      setEffectivePermissions(userPerms.effective_permissions || []);
    } catch (error) {
      toast.error('Failed to load permissions data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/rbac/users/${user.id}/permissions`, {
        role_id: selectedRoleId || null,
        custom_permissions: customPermissions,
        permission_overrides: permissionOverrides
      });
      toast.success('User permissions updated');
      onUpdate?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleCustomPermissionToggle = (perm) => {
    setCustomPermissions(prev => 
      prev.includes(perm) 
        ? prev.filter(p => p !== perm)
        : [...prev, perm]
    );
  };

  const handleOverrideToggle = (perm, grant) => {
    setPermissionOverrides(prev => {
      const newOverrides = { ...prev };
      if (newOverrides[perm] === grant) {
        delete newOverrides[perm];
      } else {
        newOverrides[perm] = grant;
      }
      return newOverrides;
    });
  };

  const selectedRole = roles.find(r => r.id === selectedRoleId);
  const rolePermissions = selectedRole?.permissions || [];

  // Calculate what the effective permissions will be
  const previewEffectivePermissions = React.useMemo(() => {
    const perms = new Set(rolePermissions);
    customPermissions.forEach(p => perms.add(p));
    Object.entries(permissionOverrides).forEach(([p, granted]) => {
      if (granted) perms.add(p);
      else perms.delete(p);
    });
    return Array.from(perms);
  }, [rolePermissions, customPermissions, permissionOverrides]);

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-600" />
            Manage Permissions - {user?.full_name}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="role">Assign Role</TabsTrigger>
            <TabsTrigger value="custom">Custom Permissions</TabsTrigger>
            <TabsTrigger value="overrides">Permission Overrides</TabsTrigger>
          </TabsList>

          {/* Role Assignment Tab */}
          <TabsContent value="role" className="flex-1 overflow-auto mt-4">
            <div className="space-y-4">
              <div>
                <Label>Select Role</Label>
                <Select value={selectedRoleId || "no-role"} onValueChange={(v) => setSelectedRoleId(v === "no-role" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="role-select">
                    <SelectValue placeholder="Select a role..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-role">No Role (Custom Only)</SelectItem>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name} ({role.permissions?.length || 0} perms)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedRole && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <h4 className="font-medium mb-2">{selectedRole.name}</h4>
                  <p className="text-sm text-slate-600 mb-3">{selectedRole.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedRole.permissions?.slice(0, 15).map(perm => (
                      <Badge key={perm} variant="outline" className="text-xs">
                        {permissions[perm] || perm}
                      </Badge>
                    ))}
                    {selectedRole.permissions?.length > 15 && (
                      <Badge variant="outline" className="text-xs bg-slate-100">
                        +{selectedRole.permissions.length - 15} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Custom Permissions Tab */}
          <TabsContent value="custom" className="flex-1 overflow-hidden mt-4">
            <div className="mb-2">
              <p className="text-sm text-slate-600">
                Add extra permissions beyond the assigned role. These permissions are <strong>added</strong> to the role.
              </p>
            </div>
            <ScrollArea className="h-[400px] border rounded-lg p-4">
              <Accordion type="multiple" className="w-full">
                {Object.entries(permissionGroups).map(([groupName, groupPerms]) => (
                  <AccordionItem key={groupName} value={groupName}>
                    <AccordionTrigger className="hover:no-underline py-2">
                      <span className="font-medium text-sm">{groupName}</span>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-4">
                      <div className="grid grid-cols-1 gap-2">
                        {groupPerms.map(perm => {
                          const inRole = rolePermissions.includes(perm);
                          const isCustom = customPermissions.includes(perm);
                          
                          return (
                            <label 
                              key={perm} 
                              className={`flex items-center gap-3 cursor-pointer p-2 rounded-lg transition-colors ${
                                inRole ? 'bg-green-50 opacity-60' : 'hover:bg-slate-50'
                              }`}
                            >
                              <Checkbox
                                checked={isCustom || inRole}
                                disabled={inRole}
                                onCheckedChange={() => handleCustomPermissionToggle(perm)}
                              />
                              <div className="flex-1">
                                <span className="text-sm">{permissions[perm]}</span>
                                {inRole && (
                                  <Badge variant="outline" className="ml-2 text-xs bg-green-100 text-green-700">
                                    From Role
                                  </Badge>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ScrollArea>
          </TabsContent>

          {/* Permission Overrides Tab */}
          <TabsContent value="overrides" className="flex-1 overflow-hidden mt-4">
            <div className="mb-2">
              <p className="text-sm text-slate-600 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Override specific permissions - can <strong>grant</strong> or <strong>revoke</strong> individual permissions.
              </p>
            </div>
            <ScrollArea className="h-[400px] border rounded-lg p-4">
              <Accordion type="multiple" className="w-full">
                {Object.entries(permissionGroups).map(([groupName, groupPerms]) => (
                  <AccordionItem key={groupName} value={groupName}>
                    <AccordionTrigger className="hover:no-underline py-2">
                      <span className="font-medium text-sm">{groupName}</span>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-4">
                      <div className="grid grid-cols-1 gap-2">
                        {groupPerms.map(perm => {
                          const hasOverride = permissionOverrides.hasOwnProperty(perm);
                          const overrideValue = permissionOverrides[perm];
                          
                          return (
                            <div key={perm} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                              <div className="flex-1">
                                <span className="text-sm">{permissions[perm]}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant={overrideValue === true ? 'default' : 'outline'}
                                  size="sm"
                                  className={`h-7 px-2 ${overrideValue === true ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                  onClick={() => handleOverrideToggle(perm, true)}
                                >
                                  <Check className="h-3 w-3 mr-1" /> Grant
                                </Button>
                                <Button
                                  variant={overrideValue === false ? 'default' : 'outline'}
                                  size="sm"
                                  className={`h-7 px-2 ${overrideValue === false ? 'bg-red-600 hover:bg-red-700' : ''}`}
                                  onClick={() => handleOverrideToggle(perm, false)}
                                >
                                  <X className="h-3 w-3 mr-1" /> Revoke
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Summary */}
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">
              Effective permissions: <strong>{previewEffectivePermissions.length}</strong>
            </span>
            <div className="flex items-center gap-2">
              {Object.keys(permissionOverrides).length > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700">
                  {Object.keys(permissionOverrides).length} overrides
                </Badge>
              )}
              {customPermissions.length > 0 && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                  {customPermissions.length} custom
                </Badge>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-permissions-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
